// Контекст текущего пользователя.
//
// Гость — полноценный режим работы, а не ошибка: приложение продолжает жить на
// localStorage, просто без синхронизации. Поэтому статус 'guest' отделён от
// 'loading', и интерфейс не мигает формой входа, пока идёт первый запрос.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import * as api from './api';
import type { ApiResult, AuthUser } from './api';
import { MAX_VARIANTS } from '../types';

export type AuthStatus = 'loading' | 'guest' | 'user';

/**
 * Гостю доступны два варианта поездки из четырёх. Ограничение живёт здесь, а не
 * в types.ts: MAX_VARIANTS — предел модели данных, а это правило продукта.
 */
export const GUEST_MAX_VARIANTS = 2;

export interface Auth {
  status: AuthStatus;
  user: AuthUser | null;
  /**
   * Администратор, вошедший «от имени» текущего пользователя. null — обычный
   * вход. Пока он заполнен, интерфейс показывает полосу-предупреждение: со
   * стороны такая сессия ничем не отличается от собственной, и об этом надо
   * напоминать.
   */
  impersonator: AuthUser | null;
  isAdmin: boolean;
  /** true, пока пользователь не вошёл (в том числе во время первой загрузки). */
  isGuest: boolean;
  /** Сколько вариантов поездки можно завести при текущем режиме. */
  maxVariants: number;
  /** Открыть окно входа из любого места — например с заблокированной кнопки. */
  requestSignIn(): void;
  /** Запрошено ли окно входа; рисует его <AuthGate>. */
  signInRequested: boolean;
  dismissSignIn(): void;
  signIn(input: { email: string; password: string }): Promise<ApiResult<{ user: AuthUser }>>;
  signUp(input: {
    email: string;
    password: string;
    name: string;
    lang: string;
  }): Promise<ApiResult<{ user: AuthUser }>>;
  signOut(): Promise<void>;
  /** Перечитать пользователя с сервера (после подтверждения почты и т.п.). */
  refresh(): Promise<void>;
  /** Локально обновить пользователя ответом, который уже пришёл с сервера. */
  applyUser(user: AuthUser): void;
}

const AuthContext = createContext<Auth | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [impersonator, setImpersonator] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  // Окно входа живёт здесь: его открывают и кнопка аккаунта, и любые
  // заблокированные для гостя действия.
  const [signInOpen, setSignInOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await api.fetchMe();
    if (res.ok && res.value?.user) {
      setUser(res.value.user);
      setImpersonator(res.value.impersonator ?? null);
      setStatus('user');
    } else {
      setUser(null);
      setImpersonator(null);
      setStatus('guest');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const signIn = useCallback<Auth['signIn']>(async (input) => {
    const res = await api.login(input);
    if (res.ok && res.value?.user) {
      setUser(res.value.user);
      setImpersonator(null);
      setStatus('user');
    }
    return res;
  }, []);

  const signUp = useCallback<Auth['signUp']>(async (input) => {
    const res = await api.register(input);
    if (res.ok && res.value?.user) {
      setUser(res.value.user);
      setImpersonator(null);
      setStatus('user');
    }
    return res;
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setUser(null);
    setImpersonator(null);
    setStatus('guest');
  }, []);

  const applyUser = useCallback((next: AuthUser) => {
    setUser(next);
    setStatus('user');
  }, []);

  const requestSignIn = useCallback(() => setSignInOpen(true), []);
  const dismissSignIn = useCallback(() => setSignInOpen(false), []);

  const value = useMemo<Auth>(
    () => ({
      status,
      user,
      impersonator,
      // Права администратора — у того, кто в аккаунте сейчас. В режиме «от
      // имени» это пользователь, и админка ему закрыта (сервер отвечает на неё
      // 403 'impersonating', интерфейс не должен обещать другого).
      isAdmin: user?.role === 'admin' && !impersonator,
      isGuest: !user,
      // Персональный лимит из админки сильнее общего MAX_VARIANTS; источник
      // правды — сервер, он проверяет то же значение при сохранении состояния.
      maxVariants: user ? (user.limits?.maxVariants ?? MAX_VARIANTS) : GUEST_MAX_VARIANTS,
      requestSignIn,
      signInRequested: signInOpen && !user,
      dismissSignIn,
      signIn,
      signUp,
      signOut,
      refresh: load,
      applyUser,
    }),
    [
      status,
      user,
      impersonator,
      signInOpen,
      requestSignIn,
      dismissSignIn,
      signIn,
      signUp,
      signOut,
      load,
      applyUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth() должен использоваться внутри <AuthProvider>');
  return ctx;
}
